import { useTranslation } from 'react-i18next';
import { FunctionComponent, useEffect, useState } from 'react';
import { Button, Checkbox, Col, Divider, Flex, Form, Input, Row, Typography } from 'antd';

import VerificationModal from 'modules/main/actions/verificationModal';
import PriceComponent from './priceComponent';
import StatusComponent from 'components/status/status';
import PhoneInputComponent, { PhoneInputReturn } from 'components/phoneInput/phoneInput';
import FormElement from 'components/formElement/formElement';
import CompoundElement from 'components/compoundElement/compoundElement';
import DisabledElement from 'components/disabledElement/disabledElement';

import { checkPhoneValidation } from 'utils/validators/phone';
import { onCheckFractionTransfer, onCheckShareholderForProfitCapitalization, onMakeTransferRequest } from '../services/profitCapitalization';
import NotificationService from 'core/services/notification';
import { mapResponseErrors } from 'utils/mapResponseErrors';
import { StatusColors } from 'utils/statusColors';
import { isRTL } from 'core/services/language';
import { formatErrorMessages } from 'utils/errorMessage';

import { FractionTransfer, ProfitCapitalization, TransferRequestPayload } from '../models/profitCapitalization';
import { VerificationPayload } from 'modules/main/actions/commonModels';
import { TranslatedField } from 'core/models/common';

interface ProfitCapitalizationTransferComponentProps {
	raiseCapital: ProfitCapitalization;
	onReload: () => void;
}

const ProfitCapitalizationTransferComponent: FunctionComponent<ProfitCapitalizationTransferComponentProps> = ({ raiseCapital, onReload }) => {
	const { t } = useTranslation();
	const [createForm] = Form.useForm();

	const sharePriceWatcher = Form.useWatch('share_price', createForm);

	const [isTransferring, setIsTransferring] = useState<boolean>(false);
	const [fractionTransfer, setFractionTransfer] = useState<FractionTransfer>();

	const [shareholderName, setShareholderName] = useState<TranslatedField>();
	const [shareholderChecking, setShareholderChecking] = useState(false);
	const [shareholderChecked, setShareholderChecked] = useState<boolean>(false);

	const [acknowledge, setAcknowledge] = useState<boolean>(false);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [verificationModalOpen, setVerificationModalOpen] = useState<boolean>(false);

	useEffect(() => {
		checkTransferStatus();
	}, []);

	const validatePhone = (_: any, value: any) => {
		if (checkPhoneValidation(createForm.getFieldValue('phone_country_code'), value)) {
			return Promise.resolve();
		} else if (value) return Promise.reject(new Error(t('COMMON.PHONE_INVALID')));
		else return Promise.reject();
	};

	const checkTransferStatus = async () => {
		try {
			const response = await onCheckFractionTransfer(raiseCapital.action.id);
			setFractionTransfer(response);
		} catch (error) {}
	};

	const transferHandler = () => {
		setIsTransferring(true);

		setTimeout(() => {
			window.scrollTo({ behavior: 'smooth', top: 5000 });
		}, 100);
	};

	const onChangePhone = ({ number, dialCode }: PhoneInputReturn) => {
		createForm.setFieldsValue({
			phone_number: number,
			phone_country_code: dialCode,
		});
	};

	const checkShareholderNationalId = async (nationalId: string) => {
		try {
			setShareholderChecking(true);

			const response = await onCheckShareholderForProfitCapitalization(nationalId);

			if (response.exists) {
				setShareholderName(response.name);
			}

			setShareholderChecked(true);
			createForm.setFieldsValue({ national_id: nationalId });
			setShareholderChecking(false);
		} catch (error: any) {
			setShareholderChecking(false);

			if (error?.status == 422) {
				NotificationService.error(t('COMMON.ERROR'), formatErrorMessages(error));
				return false;
			}

			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const transferHandlerSubmit = async (data: VerificationPayload) => {
		try {
			setIsSubmitting(true);

			const payload: TransferRequestPayload = {
				otp: data.key,
				national_id: createForm.getFieldValue('national_id'),
				raise_capital_action_id: raiseCapital.action.id,
				share_price: createForm.getFieldValue('share_price'),
			};

			if (!shareholderName) {
				payload.name = createForm.getFieldValue('name');
				payload.phone_country_code = createForm.getFieldValue('phone_country_code');
				payload.phone_number = createForm.getFieldValue('phone_number');
			}

			await onMakeTransferRequest(payload);

			checkTransferStatus();
			onReload();

			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.TRANSFER_REQUEST_CREATED_SUCCESSFULLY'));

			setIsSubmitting(false);
		} catch (error) {
			setVerificationModalOpen(false);
			setIsSubmitting(false);
			mapResponseErrors(error, createForm);

			NotificationService.error(t('COMMON.ERROR'), t('ACTIONS.ERROR_CREATING_TRANSFER_REQUEST'));
		}
	};

	return (
		<>
			<Divider />

			<h6 className="fw-medium mb-4">{t('ACTIONS.FRACTION_TRANSFER')}</h6>

			{fractionTransfer && !fractionTransfer?.isFractionTransferAvailable ? (
				<Flex justify="space-between" align="center">
					<Flex gap={40} wrap>
						<div>
							<Flex gap={10} align="center" className="mb-2">
								<h6 className="mb-0">{t('ACTIONS.BUYER_ACCEPTANCE_STATUS')}</h6>
								<StatusComponent
									small
									color={StatusColors[fractionTransfer?.transfer_status_key]}
									text={t(`COMMON.${fractionTransfer.transfer_status_key?.toUpperCase()}`)}
								/>
							</Flex>
							<Flex gap={30} align="center">
								<p className="mb-0 text-light">{fractionTransfer?.date}</p>
								<p className="mb-0 fw-medium text-primary">{isRTL() ? fractionTransfer?.buyer_name.ar : fractionTransfer?.buyer_name.en}</p>
							</Flex>
						</div>
						<div>
							<DisabledElement value={fractionTransfer?.price_per_share} label={t('ACTIONS.PRICE_PER_SHARE')} showPrice />
						</div>
					</Flex>

					{(fractionTransfer?.transfer_status_key === 'Expired' || fractionTransfer?.transfer_status_key === 'Rejected') && (
						<Button
							type="primary"
							size="middle"
							onClick={() => {
								setIsTransferring(true);
								setFractionTransfer(undefined);
							}}>
							{t('ACTIONS.CREATE_NEW_REQUEST')}
						</Button>
					)}
				</Flex>
			) : (
				<>
					{isTransferring ? (
						<Form form={createForm} initialValues={{ share_price: 0 }}>
							<Row gutter={[16, 16]}>
								<Col span={24} md={12} lg={8}>
									<CompoundElement
										buttonText={t('COMMON.CHECK')}
										label={t('ACTIONS.BUYER_NATIONAL_ID')}
										isLoading={shareholderChecking}
										onSubmit={checkShareholderNationalId}
										onChange={() => {
											setShareholderChecked(false);
											setShareholderName(undefined);
										}}
									/>
									<Form.Item hidden noStyle rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]} name="national_id" />
								</Col>

								{shareholderChecked && (
									<>
										{shareholderName ? (
											<>
												<Col span={24} md={12} lg={8}>
													<DisabledElement value={isRTL() ? shareholderName.ar : shareholderName.en} label={t('COMMON.NAME')} />
												</Col>
											</>
										) : (
											<>
												<Col span={24} md={12} lg={8}>
													<FormElement
														name="name"
														label={t('ACTIONS.BUYER_NAME')}
														rules={[
															{
																required: true,
																message: t('COMMON.FIELD_REQUIRED'),
															},
															{
																pattern: /^[^\s\d!@#$%^&*()_+={}\[\]:;"'<>?,.\\/|`~]+(?:\s+[^\s\d!@#$%^&*()_+={}\[\]:;"'<>?,.\\/|`~]+){2,}\s*$/,
																message: t('REGISTER.THREE_WORDS_VALIDATION'),
															},
														]}
														type="text"
													/>
												</Col>

												<Col span={24} md={12} lg={8}>
													<Form.Item
														name="phone_number"
														style={{ maxWidth: '100%' }}
														rules={[{ required: true, message: t('COMMON.PHONE_REQUIRED') }, { validator: validatePhone }]}>
														<PhoneInputComponent onChange={onChangePhone} fullWidth />
													</Form.Item>
													<Form.Item name="phone_country_code" hidden>
														<Input />
													</Form.Item>
												</Col>
											</>
										)}

										<Col span={24}>
											<Row gutter={[16, 16]}>
												<Col span={24} md={12} lg={8}>
													<DisabledElement
														label={t('ACTIONS.TOTAL_NUMBER_OF_UNIT_ISSUED')}
														value={raiseCapital?.action.fraction_shares}
														addonAfter={<span>{t('COMMON.SHARES')}</span>}
													/>
												</Col>

												<Col span={24} md={12} lg={8}>
													<Typography.Text style={{ marginBottom: 5, display: 'block', fontSize: 15 }}>{t('ACTIONS.SHARE_PRICE')}</Typography.Text>
													<PriceComponent form={createForm} fieldName="share_price" inputWidth={90} />
												</Col>
											</Row>
										</Col>
									</>
								)}

								<Col span={24}>
									<Flex justify="space-between" align="center" className="mt-5">
										<Checkbox onChange={(event) => setAcknowledge(event.target.checked)}>
											<span className="fw-semibold d-block">{t('MORTGAGE.AGREEMENT_1')}</span>
											<span className="text-light">{t('MORTGAGE.AGREEMENT_2')}</span>
										</Checkbox>
										<Button
											type="primary"
											className="px-4"
											disabled={!acknowledge || !shareholderChecked}
											onClick={async () => {
												try {
													await createForm.validateFields();
													setVerificationModalOpen(true);
												} catch (error) {}
											}}>
											{t('ACTIONS.TRANSFER')}
										</Button>
									</Flex>
								</Col>
							</Row>

							<VerificationModal
								otpOnly
								open={verificationModalOpen}
								isLoading={isSubmitting}
								className="confirmationModal"
								onCancel={() => setVerificationModalOpen(false)}
								onVerificationSuccess={transferHandlerSubmit}
								module="create_capitalRaise_transfer_share_fractions"
								note={t('ACTIONS.RAISE_CAPITAL_NOTE')}
								noteType="red"
							/>
						</Form>
					) : (
						<Flex align="center" justify="space-between">
							<div>
								<p className="mb-0">{t('ACTIONS.FRACTION_TRANSFER_TITLE')}</p>
								<p className="mb-0 text-light">{t('ACTIONS.FRACTION_TRANSFER_DESC')}</p>
							</div>

							<Button type="primary" size="middle" className="px-4 py-3" onClick={transferHandler}>
								{t('ACTIONS.CREATE_TRANSFER_REQUEST')}
							</Button>
						</Flex>
					)}
				</>
			)}
		</>
	);
};

export default ProfitCapitalizationTransferComponent;
